import React, { useState, useEffect } from 'react';
import {
  subscribe,
  getSupplierAssessments,
  getSupplierById,
  getAssessmentCriteria,
  getAssessmentSchedule,
  getAssessmentTemplates,
  getBenchmarks,
  getRiskMatrix,
  getAssessmentOverview,
  getHighRiskSuppliers,
  getTopPerformers,
  getSuppliersBelowThreshold,
  getAssessmentTrends,
  getScoreGrade,
  benchmarkSupplier,
  scheduleAssessment,
  updateAssessmentScore,
  addAuditFinding,
  updateImprovementPlan,
  exportAssessmentReport,
  exportBulkAssessmentData,
  refreshAssessmentData
} from '../../../services/ethics/supplierAssessmentService';
import { add as addAuditLog } from '../../../services/bread/auditLogService';

const SupplierAssessment = () => {
  const [assessmentData, setAssessmentData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterCriteria, setFilterCriteria] = useState({
    riskLevel: '',
    category: '',
    minScore: '',
    sortBy: 'overallScore'
  });

  useEffect(() => {
    const unsubscribe = subscribe(setAssessmentData);
    setLoading(false);
    
    // Log access
    addAuditLog({ 
      user: 'feros.khan@krotrust.org', 
      action: 'page_access', 
      target: 'supplier_assessment', 
      status: 'success' 
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const handleRefreshData = async () => {
    setLoading(true);
    await refreshAssessmentData();
    addAuditLog({ 
      user: 'feros.khan@krotrust.org', 
      action: 'data_refresh', 
      target: 'supplier_assessment_data', 
      status: 'success' 
    });
    setTimeout(() => setLoading(false), 1000);
  };

  const handleSupplierSelect = (supplier) => {
    setSelectedSupplier(supplier);
    setActiveTab('supplier-detail');
  };

  const handleExportReport = (supplierId, format = 'json') => {
    const reportData = exportAssessmentReport(supplierId, format);
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `supplier_assessment_${supplierId}_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleBulkExport = () => {
    const bulkData = exportBulkAssessmentData(filterCriteria);
    const blob = new Blob([JSON.stringify(bulkData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk_supplier_assessments_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading || !assessmentData) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid rgba(124, 58, 237, 0.3)',
            borderTop: '4px solid #7c3aed',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p style={{ color: '#cbd5e1', fontSize: '18px', fontWeight: '600' }}>
            Loading Supplier Assessment System...
          </p>
        </div>
      </div>
    );
  }

  const suppliers = getSupplierAssessments();
  const assessmentOverview = getAssessmentOverview();
  const highRiskSuppliers = getHighRiskSuppliers();
  const topPerformers = getTopPerformers();
  const belowThreshold = getSuppliersBelowThreshold();
  const assessmentSchedule = getAssessmentSchedule();
  const criteria = getAssessmentCriteria();
  const templates = getAssessmentTemplates();
  const benchmarks = getBenchmarks();
  const riskMatrix = getRiskMatrix();

  // Filter suppliers based on criteria
  const filteredSuppliers = suppliers.filter(supplier => {
    if (filterCriteria.riskLevel && supplier.riskLevel !== filterCriteria.riskLevel) return false;
    if (filterCriteria.category && supplier.category !== filterCriteria.category) return false;
    if (filterCriteria.minScore && supplier.currentScores.overall < parseInt(filterCriteria.minScore)) return false;
    return true;
  }).sort((a, b) => {
    switch (filterCriteria.sortBy) {
      case 'overallScore':
        return b.currentScores.overall - a.currentScores.overall;
      case 'riskLevel':
        const riskOrder = { 'Low': 1, 'Medium': 2, 'High': 3 };
        return riskOrder[b.riskLevel] - riskOrder[a.riskLevel];
      case 'assessmentDate':
        return new Date(b.lastAssessment) - new Date(a.lastAssessment);
      case 'annualSpend':
        return b.annualSpend - a.annualSpend;
      default:
        return 0;
    }
  });

  const OverviewTab = () => (
    <div style={{ padding: '0' }}>
      {/* Assessment Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        {/* Assessment Overview */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '25px',
          border: '2px solid rgba(124, 58, 237, 0.3)',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
        }}>
          <h3 style={{
            color: '#ffffff',
            fontSize: '18px',
            fontWeight: '800',
            marginBottom: '20px',
            textShadow: '0 1px 3px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '20px' }}>📊</span>
            Assessment Overview
          </h3>
          
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{
              fontSize: '48px',
              fontWeight: '900',
              color: '#a855f7',
              textShadow: '0 2px 8px rgba(168, 85, 247, 0.5)',
              marginBottom: '8px'
            }}>
              {assessmentOverview.totalSuppliers}
            </div>
            <div style={{
              color: '#94a3b8',
              fontSize: '13px',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              Total Suppliers
            </div>
          </div>
          
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{
              fontSize: '32px',
              fontWeight: '900',
              color: '#22c55e',
              marginBottom: '5px'
            }}>
              {assessmentOverview.averageScore}
            </div>
            <div style={{
              color: '#94a3b8',
              fontSize: '12px',
              fontWeight: '700',
              textTransform: 'uppercase'
            }}>
              Average ESG Score
            </div>
          </div>
          
          <div style={{
            height: '10px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '5px',
            overflow: 'hidden',
            marginBottom: '10px'
          }}>
            <div style={{
              width: `${assessmentOverview.assessmentCompletion}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)',
              borderRadius: '5px',
              boxShadow: '0 0 10px rgba(34, 197, 94, 0.5)'
            }}></div>
          </div>
          <div style={{
            color: '#cbd5e1',
            fontSize: '13px',
            fontWeight: '700',
            textAlign: 'center'
          }}>
            {assessmentOverview.assessmentCompletion}% Assessment Complete
          </div>
        </div>

        {/* Risk Distribution */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '25px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
        }}>
          <h3 style={{
            color: '#ffffff',
            fontSize: '18px',
            fontWeight: '800',
            marginBottom: '20px',
            textShadow: '0 1px 3px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '20px' }}>⚠️</span>
            Risk Distribution
          </h3>
          
          <div style={{ marginBottom: '0' }}>
            <div className="mb-3">
              <div className="d-flex justify-content-between">
                <span className="text-success">Low Risk</span>
                <span className="fw-bold">{assessmentOverview.riskDistribution.low || 0}</span>
              </div>
              <div className="progress mb-2" style={{ height: '6px' }}>
                <div 
                  className="progress-bar bg-success" 
                  style={{ width: `${((assessmentOverview.riskDistribution.low || 0) / assessmentOverview.totalSuppliers) * 100}%` }}
                ></div>
              </div>
            </div>
            <div className="mb-3">
              <div className="d-flex justify-content-between">
                <span className="text-warning">Medium Risk</span>
                <span className="fw-bold">{assessmentOverview.riskDistribution.medium || 0}</span>
              </div>
              <div className="progress mb-2" style={{ height: '6px' }}>
                <div 
                  className="progress-bar bg-warning" 
                  style={{ width: `${((assessmentOverview.riskDistribution.medium || 0) / assessmentOverview.totalSuppliers) * 100}%` }}
                ></div>
              </div>
            </div>
            <div className="mb-3">
              <div className="d-flex justify-content-between">
                <span className="text-danger">High Risk</span>
                <span className="fw-bold">{assessmentOverview.riskDistribution.high || 0}</span>
              </div>
              <div className="progress mb-2" style={{ height: '6px' }}>
                <div 
                  className="progress-bar bg-danger" 
                  style={{ width: `${((assessmentOverview.riskDistribution.high || 0) / assessmentOverview.totalSuppliers) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Category Averages */}
      <div className="col-md-3 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header">
            <h6 className="mb-0">
              <i className="fas fa-chart-bar me-2"></i>ESG Category Averages
            </h6>
          </div>
          <div className="card-body">
            <div className="mb-3">
              <div className="d-flex justify-content-between mb-1">
                <span>Environmental</span>
                <span className="fw-bold">{Math.round(assessmentOverview.categoryAverages.environmental)}</span>
              </div>
              <div className="progress mb-2" style={{ height: '6px' }}>
                <div 
                  className="progress-bar bg-success" 
                  style={{ width: `${assessmentOverview.categoryAverages.environmental}%` }}
                ></div>
              </div>
            </div>
            <div className="mb-3">
              <div className="d-flex justify-content-between mb-1">
                <span>Social</span>
                <span className="fw-bold">{Math.round(assessmentOverview.categoryAverages.social)}</span>
              </div>
              <div className="progress mb-2" style={{ height: '6px' }}>
                <div 
                  className="progress-bar bg-info" 
                  style={{ width: `${assessmentOverview.categoryAverages.social}%` }}
                ></div>
              </div>
            </div>
            <div className="mb-3">
              <div className="d-flex justify-content-between mb-1">
                <span>Governance</span>
                <span className="fw-bold">{Math.round(assessmentOverview.categoryAverages.governance)}</span>
              </div>
              <div className="progress mb-2" style={{ height: '6px' }}>
                <div 
                  className="progress-bar bg-warning" 
                  style={{ width: `${assessmentOverview.categoryAverages.governance}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upcoming Assessments */}
      <div className="col-md-3 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header">
            <h6 className="mb-0">
              <i className="fas fa-calendar-alt me-2"></i>Upcoming Assessments
            </h6>
          </div>
          <div className="card-body">
            <div className="text-center mb-3">
              <div className="h3 text-info">{assessmentOverview.upcomingAssessments}</div>
              <small className="text-muted">Due in 90 Days</small>
            </div>
            {assessmentSchedule.slice(0, 3).map((assessment, index) => (
              <div key={index} className="mb-2 p-2 border rounded">
                <div className="fw-bold small">{assessment.supplierName}</div>
                <div className="d-flex justify-content-between">
                  <small className="text-muted">{assessment.assessmentType}</small>
                  <small className={`fw-bold ${
                    assessment.status === 'Urgent' ? 'text-danger' : 
                    assessment.status === 'Scheduled' ? 'text-success' : 'text-warning'
                  }`}>
                    {new Date(assessment.dueDate).toLocaleDateString()}
                  </small>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* High Risk Suppliers Alert */}
      {highRiskSuppliers.length > 0 && (
        <div className="col-12 mb-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-danger text-white">
              <h5 className="mb-0">
                <i className="fas fa-exclamation-triangle me-2"></i>High Risk Suppliers Requiring Immediate Attention
              </h5>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead className="table-light">
                    <tr>
                      <th>Supplier</th>
                      <th>Category</th>
                      <th>Location</th>
                      <th>ESG Score</th>
                      <th>Risk Factors</th>
                      <th>Last Assessment</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {highRiskSuppliers.map((supplier, index) => (
                      <tr key={index}>
                        <td>
                          <div className="fw-bold">{supplier.name}</div>
                          <small className="text-muted">{supplier.id}</small>
                        </td>
                        <td><span className="badge bg-secondary">{supplier.category}</span></td>
                        <td>{supplier.location}</td>
                        <td>
                          <span className="badge bg-danger">
                            {supplier.currentScores.overall}
                          </span>
                        </td>
                        <td>
                          {supplier.riskFactors.slice(0, 2).map((risk, idx) => (
                            <div key={idx} className="small">
                              <span className={`badge badge-sm ${
                                risk.level === 'High' ? 'bg-danger' : 
                                risk.level === 'Medium' ? 'bg-warning' : 'bg-success'
                              }`}>
                                {risk.factor}
                              </span>
                            </div>
                          ))}
                        </td>
                        <td>{new Date(supplier.lastAssessment).toLocaleDateString()}</td>
                        <td>
                          <button 
                            className="btn btn-sm btn-outline-primary me-1"
                            onClick={() => handleSupplierSelect(supplier)}
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          <button 
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => handleExportReport(supplier.id)}
                          >
                            <i className="fas fa-download"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top Performers */}
      <div className="col-md-6 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-success text-white">
            <h5 className="mb-0">
              <i className="fas fa-star me-2"></i>Top Performing Suppliers
            </h5>
          </div>
          <div className="card-body">
            {topPerformers.slice(0, 5).map((supplier, index) => {
              const grade = getScoreGrade(supplier.currentScores.overall);
              return (
                <div key={index} className="mb-3 p-3 border rounded">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div>
                      <h6 className="mb-0">{supplier.name}</h6>
                      <small className="text-muted">{supplier.category} • {supplier.location}</small>
                    </div>
                    <div className="text-center">
                      <div className="h4 mb-0" style={{ color: grade.color }}>
                        {grade.grade}
                      </div>
                      <small className="text-muted">{supplier.currentScores.overall}</small>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-4">
                      <div className="text-center">
                        <div className="small fw-bold">{supplier.currentScores.environmental.total}</div>
                        <div className="progress mt-1" style={{ height: '4px' }}>
                          <div 
                            className="progress-bar bg-success" 
                            style={{ width: `${supplier.currentScores.environmental.total}%` }}
                          ></div>
                        </div>
                        <small className="text-muted">Environmental</small>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="text-center">
                        <div className="small fw-bold">{supplier.currentScores.social.total}</div>
                        <div className="progress mt-1" style={{ height: '4px' }}>
                          <div 
                            className="progress-bar bg-info" 
                            style={{ width: `${supplier.currentScores.social.total}%` }}
                          ></div>
                        </div>
                        <small className="text-muted">Social</small>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="text-center">
                        <div className="small fw-bold">{supplier.currentScores.governance.total}</div>
                        <div className="progress mt-1" style={{ height: '4px' }}>
                          <div 
                            className="progress-bar bg-warning" 
                            style={{ width: `${supplier.currentScores.governance.total}%` }}
                          ></div>
                        </div>
                        <small className="text-muted">Governance</small>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Suppliers Below Threshold */}
      <div className="col-md-6 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-warning text-dark">
            <h5 className="mb-0">
              <i className="fas fa-exclamation-circle me-2"></i>Suppliers Needing Improvement
            </h5>
          </div>
          <div className="card-body">
            {belowThreshold.slice(0, 5).map((supplier, index) => {
              const grade = getScoreGrade(supplier.currentScores.overall);
              return (
                <div key={index} className="mb-3 p-3 border rounded">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div>
                      <h6 className="mb-0">{supplier.name}</h6>
                      <small className="text-muted">{supplier.category} • {supplier.location}</small>
                    </div>
                    <div className="text-center">
                      <div className="h4 mb-0" style={{ color: grade.color }}>
                        {grade.grade}
                      </div>
                      <small className="text-muted">{supplier.currentScores.overall}</small>
                    </div>
                  </div>
                  <div className="mb-2">
                    <small className="fw-bold text-danger">Key Issues:</small>
                    <div className="d-flex flex-wrap">
                      {supplier.improvementPlan.actions.slice(0, 2).map((action, idx) => (
                        <span key={idx} className="badge bg-warning text-dark me-1 mb-1">
                          {action.category}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="d-flex justify-content-between">
                    <small>Improvement Progress</small>
                    <small>
                      {Math.round(supplier.improvementPlan.actions.reduce((sum, a) => sum + a.progress, 0) / supplier.improvementPlan.actions.length)}%
                    </small>
                  </div>
                  <div className="progress mt-1" style={{ height: '4px' }}>
                    <div 
                      className="progress-bar bg-info" 
                      style={{ width: `${Math.round(supplier.improvementPlan.actions.reduce((sum, a) => sum + a.progress, 0) / supplier.improvementPlan.actions.length)}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  const SuppliersTab = () => (
    <div className="row">
      {/* Filter Controls */}
      <div className="col-12 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-filter me-2"></i>Filter & Sort Suppliers
            </h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-3">
                <label className="form-label">Risk Level</label>
                <select 
                  className="form-select"
                  value={filterCriteria.riskLevel}
                  onChange={(e) => setFilterCriteria({...filterCriteria, riskLevel: e.target.value})}
                >
                  <option value="">All Risk Levels</option>
                  <option value="Low">Low Risk</option>
                  <option value="Medium">Medium Risk</option>
                  <option value="High">High Risk</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Category</label>
                <select 
                  className="form-select"
                  value={filterCriteria.category}
                  onChange={(e) => setFilterCriteria({...filterCriteria, category: e.target.value})}
                >
                  <option value="">All Categories</option>
                  <option value="Primary Manufacturing">Primary Manufacturing</option>
                  <option value="Component Manufacturing">Component Manufacturing</option>
                  <option value="Logistics & Transportation">Logistics & Transportation</option>
                  <option value="Technology Services">Technology Services</option>
                  <option value="Mining & Extraction">Mining & Extraction</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Minimum Score</label>
                <select 
                  className="form-select"
                  value={filterCriteria.minScore}
                  onChange={(e) => setFilterCriteria({...filterCriteria, minScore: e.target.value})}
                >
                  <option value="">No Minimum</option>
                  <option value="90">90+ (Excellent)</option>
                  <option value="80">80+ (Very Good)</option>
                  <option value="70">70+ (Good)</option>
                  <option value="60">60+ (Satisfactory)</option>
                  <option value="50">50+ (Needs Improvement)</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Sort By</label>
                <select 
                  className="form-select"
                  value={filterCriteria.sortBy}
                  onChange={(e) => setFilterCriteria({...filterCriteria, sortBy: e.target.value})}
                >
                  <option value="overallScore">Overall Score</option>
                  <option value="riskLevel">Risk Level</option>
                  <option value="assessmentDate">Assessment Date</option>
                  <option value="annualSpend">Annual Spend</option>
                </select>
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-12">
                <button 
                  className="btn btn-outline-secondary me-2"
                  onClick={() => setFilterCriteria({ riskLevel: '', category: '', minScore: '', sortBy: 'overallScore' })}
                >
                  <i className="fas fa-times me-2"></i>Clear Filters
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleBulkExport}
                >
                  <i className="fas fa-download me-2"></i>Export Filtered Data
                </button>
                <span className="badge bg-info ms-3">
                  {filteredSuppliers.length} suppliers found
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Suppliers Table */}
      <div className="col-12">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-building me-2"></i>Supplier Assessment Results
            </h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Supplier</th>
                    <th>Category</th>
                    <th>Location</th>
                    <th>Tier</th>
                    <th>Annual Spend</th>
                    <th>Overall Score</th>
                    <th>E</th>
                    <th>S</th>
                    <th>G</th>
                    <th>Risk Level</th>
                    <th>Last Assessment</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSuppliers.map((supplier, index) => {
                    const grade = getScoreGrade(supplier.currentScores.overall);
                    return (
                      <tr key={index}>
                        <td>
                          <div className="fw-bold">{supplier.name}</div>
                          <small className="text-muted">{supplier.id}</small>
                        </td>
                        <td><span className="badge bg-secondary">{supplier.category}</span></td>
                        <td>{supplier.location}</td>
                        <td><span className="badge bg-info">{supplier.tier}</span></td>
                        <td>${(supplier.annualSpend / 1000000).toFixed(1)}M</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <span 
                              className="badge me-2" 
                              style={{ backgroundColor: grade.color, color: 'white' }}
                            >
                              {grade.grade}
                            </span>
                            <span className="fw-bold">{supplier.currentScores.overall}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${
                            supplier.currentScores.environmental.total >= 80 ? 'bg-success' : 
                            supplier.currentScores.environmental.total >= 60 ? 'bg-warning' : 'bg-danger'
                          }`}>
                            {supplier.currentScores.environmental.total}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${
                            supplier.currentScores.social.total >= 80 ? 'bg-success' : 
                            supplier.currentScores.social.total >= 60 ? 'bg-warning' : 'bg-danger'
                          }`}>
                            {supplier.currentScores.social.total}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${
                            supplier.currentScores.governance.total >= 80 ? 'bg-success' : 
                            supplier.currentScores.governance.total >= 60 ? 'bg-warning' : 'bg-danger'
                          }`}>
                            {supplier.currentScores.governance.total}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${
                            supplier.riskLevel === 'Low' ? 'bg-success' : 
                            supplier.riskLevel === 'Medium' ? 'bg-warning' : 'bg-danger'
                          }`}>
                            {supplier.riskLevel}
                          </span>
                        </td>
                        <td>{new Date(supplier.lastAssessment).toLocaleDateString()}</td>
                        <td>
                          <button 
                            className="btn btn-sm btn-outline-primary me-1"
                            onClick={() => handleSupplierSelect(supplier)}
                            title="View Details"
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          <button 
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => handleExportReport(supplier.id)}
                            title="Export Report"
                          >
                            <i className="fas fa-download"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const CriteriaTab = () => (
    <div className="row">
      <div className="col-md-4 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-success text-white">
            <h5 className="mb-0">
              <i className="fas fa-leaf me-2"></i>Environmental Criteria
            </h5>
            <small>Weight: {criteria.environmental.totalWeight}% of overall score</small>
          </div>
          <div className="card-body">
            {criteria.environmental.categories.map((category, index) => (
              <div key={index} className="mb-3 p-2 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="fw-bold small">{category.name}</span>
                  <span className="badge bg-success">{category.weight}%</span>
                </div>
                <div className="progress" style={{ height: '4px' }}>
                  <div 
                    className="progress-bar bg-success" 
                    style={{ width: `${category.weight}%` }}
                  ></div>
                </div>
                <small className="text-muted">Max Score: {category.maxScore}</small>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="col-md-4 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-info text-white">
            <h5 className="mb-0">
              <i className="fas fa-users me-2"></i>Social Criteria
            </h5>
            <small>Weight: {criteria.social.totalWeight}% of overall score</small>
          </div>
          <div className="card-body">
            {criteria.social.categories.map((category, index) => (
              <div key={index} className="mb-3 p-2 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="fw-bold small">{category.name}</span>
                  <span className="badge bg-info">{category.weight}%</span>
                </div>
                <div className="progress" style={{ height: '4px' }}>
                  <div 
                    className="progress-bar bg-info" 
                    style={{ width: `${category.weight}%` }}
                  ></div>
                </div>
                <small className="text-muted">Max Score: {category.maxScore}</small>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="col-md-4 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-warning text-dark">
            <h5 className="mb-0">
              <i className="fas fa-balance-scale me-2"></i>Governance Criteria
            </h5>
            <small>Weight: {criteria.governance.totalWeight}% of overall score</small>
          </div>
          <div className="card-body">
            {criteria.governance.categories.map((category, index) => (
              <div key={index} className="mb-3 p-2 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="fw-bold small">{category.name}</span>
                  <span className="badge bg-warning text-dark">{category.weight}%</span>
                </div>
                <div className="progress" style={{ height: '4px' }}>
                  <div 
                    className="progress-bar bg-warning" 
                    style={{ width: `${category.weight}%` }}
                  ></div>
                </div>
                <small className="text-muted">Max Score: {category.maxScore}</small>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Assessment Templates */}
      <div className="col-12 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-clipboard-list me-2"></i>Assessment Templates
            </h5>
          </div>
          <div className="card-body">
            <div className="row">
              {Object.entries(templates).map(([key, template]) => (
                <div key={key} className="col-md-4 mb-3">
                  <div className="card border">
                    <div className="card-header">
                      <h6 className="mb-0">{template.name}</h6>
                      <small className="text-muted">Duration: {template.duration}</small>
                    </div>
                    <div className="card-body">
                      <div className="mb-2">
                        <strong className="small">Sections:</strong>
                        {template.sections.map((section, idx) => (
                          <div key={idx} className="small text-muted">• {section}</div>
                        ))}
                      </div>
                      <div className="mb-2">
                        <strong className="small">Required Documents:</strong>
                        {template.requiredDocs.slice(0, 3).map((doc, idx) => (
                          <div key={idx} className="small text-muted">• {doc}</div>
                        ))}
                        {template.requiredDocs.length > 3 && (
                          <div className="small text-muted">+ {template.requiredDocs.length - 3} more</div>
                        )}
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="badge bg-primary">Min Score: {template.minScore}</span>
                        <span className="badge bg-secondary">{template.frequency}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const ScheduleTab = () => (
    <div className="row">
      <div className="col-12">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-calendar-alt me-2"></i>Assessment Schedule
            </h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Supplier</th>
                    <th>Assessment Type</th>
                    <th>Due Date</th>
                    <th>Status</th>
                    <th>Days Until Due</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assessmentSchedule.map((assessment, index) => {
                    const daysUntilDue = Math.ceil((new Date(assessment.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
                    return (
                      <tr key={index}>
                        <td>
                          <div className="fw-bold">{assessment.supplierName}</div>
                          <small className="text-muted">{assessment.supplierId}</small>
                        </td>
                        <td>{assessment.assessmentType}</td>
                        <td>{new Date(assessment.dueDate).toLocaleDateString()}</td>
                        <td>
                          <span className={`badge ${
                            assessment.status === 'Urgent' ? 'bg-danger' : 
                            assessment.status === 'Scheduled' ? 'bg-success' : 
                            assessment.status === 'Pending' ? 'bg-warning' : 'bg-secondary'
                          }`}>
                            {assessment.status}
                          </span>
                        </td>
                        <td>
                          <span className={`fw-bold ${
                            daysUntilDue <= 7 ? 'text-danger' : 
                            daysUntilDue <= 30 ? 'text-warning' : 'text-success'
                          }`}>
                            {daysUntilDue > 0 ? `${daysUntilDue} days` : `${Math.abs(daysUntilDue)} days overdue`}
                          </span>
                        </td>
                        <td>
                          <button className="btn btn-sm btn-outline-primary me-1">
                            <i className="fas fa-play"></i> Start
                          </button>
                          <button className="btn btn-sm btn-outline-secondary">
                            <i className="fas fa-edit"></i> Reschedule
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
      padding: '0',
      margin: '0'
    }}>
      {/* Hero Header */}
      <div style={{
        background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #c084fc 100%)',
        padding: '60px 40px',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
      }}>
        {/* SVG Pattern Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>

        <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
            <div style={{ flex: 1 }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '70px',
                height: '70px',
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                borderRadius: '20px',
                marginBottom: '20px',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
              }}>
                <span style={{ fontSize: '36px' }}>📊</span>
              </div>
              <h1 style={{
                fontSize: '42px',
                fontWeight: '900',
                color: '#ffffff',
                marginBottom: '12px',
                textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
                lineHeight: '1.2'
              }}>
                Supplier Assessment
              </h1>
              <p style={{
                fontSize: '18px',
                color: 'rgba(255, 255, 255, 0.95)',
                fontWeight: '600',
                margin: 0,
                textShadow: '0 1px 3px rgba(0, 0, 0, 0.2)'
              }}>
                Comprehensive ESG supplier evaluation and risk management
              </p>
            </div>
            
            <button
              onClick={handleRefreshData}
              disabled={loading}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '12px',
                padding: '14px 28px',
                color: '#ffffff',
                fontSize: '16px',
                fontWeight: '700',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                opacity: loading ? 0.6 : 1
              }}
              onMouseOver={(e) => {
                if (!loading) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
                }
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)';
              }}
            >
              <span style={{ fontSize: '18px' }}>🔄</span>
              Refresh Data
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: 'rgba(15, 23, 42, 0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(124, 58, 237, 0.3)',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px 40px' }}>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {[
              { id: 'overview', label: 'Overview', icon: '📊' },
              { id: 'suppliers', label: 'Suppliers', icon: '🏢' },
              { id: 'criteria', label: 'Assessment Criteria', icon: '📋' },
              { id: 'schedule', label: 'Schedule', icon: '📅' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  background: activeTab === tab.id 
                    ? 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)'
                    : 'rgba(255, 255, 255, 0.05)',
                  border: activeTab === tab.id 
                    ? '2px solid rgba(168, 85, 247, 0.5)'
                    : '2px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  padding: '12px 24px',
                  color: '#ffffff',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: activeTab === tab.id 
                    ? '0 4px 15px rgba(124, 58, 237, 0.4)'
                    : '0 2px 8px rgba(0, 0, 0, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseOver={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseOut={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }
                }}
              >
                <span style={{ fontSize: '18px' }}>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '40px' }}>
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'suppliers' && <SuppliersTab />}
        {activeTab === 'criteria' && <CriteriaTab />}
        {activeTab === 'schedule' && <ScheduleTab />}
      </div>

      {/* Selected Supplier Detail Modal would go here */}
      {selectedSupplier && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-building me-2"></i>
                  {selectedSupplier.name} - Detailed Assessment
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setSelectedSupplier(null)}
                ></button>
              </div>
              <div className="modal-body">
                {/* Supplier detail content would be implemented here */}
                <p>Detailed supplier assessment view for {selectedSupplier.name}</p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setSelectedSupplier(null)}
                >
                  Close
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={() => handleExportReport(selectedSupplier.id)}
                >
                  Export Report
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierAssessment;